import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useLibraryMovies, useLibrarySeries } from '../library/useLibrary'
import {
  useContinueWatchingMovies,
  useContinueWatchingSeries,
} from '../library/useContinueWatching'
import { useBasedOnRatings } from '../discover/useRecommendations'
import { useTrendingWeek } from './useTrending'
import { useUserSettings } from '../settings/useUserSettings'
import {
  HomeHero,
  type HeroMovieSeed,
  type HeroSeriesSeed,
} from './HomeHero'
import { HomeRow } from './HomeRow'
import { GenreRotatingRow } from './GenreRotatingRow'
import { HomeTmdbPosterCard } from './HomePosterCard'
import { LoadMoreCard } from './LoadMoreCard'

/**
 * Home view layout:
 *
 *   1. Large "GOOD VIEW HUNTING" letterhead as background wordmark
 *   2. Oversized search bar (centered, prominent CTA)
 *   3. Hero pair — last in-progress movie + last in-progress series
 *   4. "Browse recommendations" gate button
 *   5. [gated — revealed on click] carousel stack:
 *        a. Top for You — Movies
 *        b. Top for You — Series
 *        c. Browse by Genre       (single smart row, chip selector)
 *        d. Popular               (mixed movies + series)
 *
 * The gate mirrors the desktop `NewDiscoverView` "Browse Recommendations"
 * affordance and persists state in the URL hash so a reload restores
 * the expanded view.
 */

interface Props {
  onOpenSearch: () => void
  onOpenDetail: (tmdbId: number, type: 'movie' | 'tv') => void
  onPersonOpen?: (personId: number) => void
}

const PAGE_SIZE = 20

export function HomeView({ onOpenSearch, onOpenDetail }: Props) {
  const { entries: libraryMovies } = useLibraryMovies()
  const { entries: librarySeries } = useLibrarySeries()

  const continueWatchingMovies = useContinueWatchingMovies(1)
  const continueWatchingSeries = useContinueWatchingSeries(1)

  const recs = useBasedOnRatings()
  const trending = useTrendingWeek()
  const { settings } = useUserSettings()
  const hasTmdbKey = Boolean(settings?.tmdbApiKey)

  const hasLibrary = libraryMovies.length > 0 || librarySeries.length > 0

  // ——— Hero seeds ————————————————————————————————————————————————
  const lastMovie = useMemo<HeroMovieSeed | null>(() => {
    const top = continueWatchingMovies[0]
    if (!top) return null
    return {
      tmdbId: top.tmdbId,
      progressSeconds:
        typeof top.entry.record.progress === 'number' ? top.entry.record.progress : 0,
    }
  }, [continueWatchingMovies])

  const lastSeries = useMemo<HeroSeriesSeed | null>(() => {
    const top = continueWatchingSeries[0]
    if (!top) return null
    return {
      tmdbId: top.tmdbId,
      lastSeason: top.furthestSeason,
      lastEpisode: top.furthestEpisode,
    }
  }, [continueWatchingSeries])

  // ——— Rating maps for badges ———————————————————————————————————
  const userMovieRatings = useMemo(() => {
    const map = new Map<number, number>()
    for (const m of libraryMovies) {
      if (typeof m.record.userRating === 'number') {
        map.set(m.tmdbId, m.record.userRating)
      }
    }
    return map
  }, [libraryMovies])

  const userSeriesRatings = useMemo(() => {
    const map = new Map<number, number>()
    for (const s of librarySeries) {
      if (typeof s.record.userRating === 'number') {
        map.set(s.tmdbId, s.record.userRating)
      }
    }
    return map
  }, [librarySeries])

  const libraryMovieIds = useMemo(
    () => new Set(libraryMovies.map((m) => m.tmdbId)),
    [libraryMovies]
  )

  // ——— Gate state ————————————————————————————————————————————————
  const [discoverOpen, setDiscoverOpen] = useState(
    () => typeof window !== 'undefined' && window.location.hash === '#discover'
  )
  const discoverRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (discoverOpen) {
      if (window.location.hash !== '#discover') {
        window.history.replaceState(null, '', '#discover')
      }
      requestAnimationFrame(() => {
        discoverRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    } else if (window.location.hash === '#discover') {
      window.history.replaceState(null, '', window.location.pathname)
    }
  }, [discoverOpen])

  const [selectedGenreId, setSelectedGenreId] = useState<number>(0)

  // Per-row pagination — each row starts with PAGE_SIZE items and
  // reveals another PAGE_SIZE each time the user clicks Load more.
  const [movieVisible, setMovieVisible] = useState(PAGE_SIZE)
  const [seriesVisible, setSeriesVisible] = useState(PAGE_SIZE)
  const [trendingVisible, setTrendingVisible] = useState(PAGE_SIZE)

  const recommendedMovieItems = useMemo(
    () => recs.movies.slice(0, movieVisible),
    [recs.movies, movieVisible]
  )
  const recommendedSeriesItems = useMemo(
    () => recs.series.slice(0, seriesVisible),
    [recs.series, seriesVisible]
  )
  const trendingItems = useMemo(
    () => trending.items.slice(0, trendingVisible),
    [trending.items, trendingVisible]
  )

  const canLoadMoreMovies = recs.movies.length > movieVisible
  const canLoadMoreSeries = recs.series.length > seriesVisible
  const canLoadMoreTrending = trending.items.length > trendingVisible

  return (
    <div className="relative pb-12">
      {/* ─── Background letterhead ──────────────────────────────── */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-0 flex select-none flex-col items-center justify-center overflow-hidden"
        style={{ height: 'clamp(240px, 40vh, 520px)' }}
      >
        <div
          className="w-full text-center font-black leading-none tracking-tighter text-white/[0.04]"
          style={{ fontSize: 'clamp(3rem, 14vw, 12rem)' }}
        >
          GOOD VIEW
        </div>
        <div
          className="w-full text-center font-black leading-none tracking-tighter text-white/[0.04]"
          style={{ fontSize: 'clamp(3rem, 14vw, 12rem)' }}
        >
          HUNTING
        </div>
      </div>

      {/* ─── 1. Search bar (oversized) ──────────────────────────── */}
      <div className="relative z-10 mx-auto w-full max-w-screen-lg px-4 pt-10 sm:pt-16">
        <HomeSearchBar onClick={onOpenSearch} />
      </div>

      {/* ─── 1b. Desktop-app CTA banner ─────────────────────────────
          First-visit dismissible banner that funnels PWA users to the
          full-fat desktop installer. Paired with the persistent
          "Desktop app" pill in the top header (see LibraryView) and
          the in-search notice. Dismissal is persisted in localStorage
          so returning users aren't nagged. */}
      <div className="relative z-10 mx-auto w-full max-w-screen-lg px-4 pt-6">
        <DownloadBanner />
      </div>

      {/* ─── 2. Hero pair ─────────────────────────────────────────── */}
      <div className="relative z-10 py-8 sm:py-12">
        <HomeHero
          lastMovie={lastMovie}
          lastSeries={lastSeries}
          onOpenDetail={onOpenDetail}
          hasLibrary={hasLibrary}
          onOpenSearch={onOpenSearch}
        />
      </div>

      {/* ─── 3. Browse recommendations gate ─────────────────────── */}
      <div className="relative z-10 mx-auto w-full max-w-screen-xl px-4 pt-2">
        <button
          type="button"
          onClick={() => setDiscoverOpen((v) => !v)}
          className="mx-auto flex items-center gap-2 rounded-full border border-border-primary bg-bg-secondary px-6 py-3 text-sm font-medium text-text-primary shadow-cinematic transition-colors hover:border-accent-primary/50 hover:bg-bg-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/60"
          aria-expanded={discoverOpen}
          aria-controls="home-discover-section"
        >
          <GridIcon />
          {discoverOpen ? 'Hide recommendations' : 'Browse Recommendations'}
          <ChevronIcon open={discoverOpen} />
        </button>
      </div>

      {/* ─── 4. Gated carousel stack ────────────────────────────── */}
      {discoverOpen && (
        <div
          ref={discoverRef}
          id="home-discover-section"
          className="relative z-10 mx-auto mt-8 w-full max-w-screen-xl space-y-8 border-t border-border-primary pt-8 scroll-mt-24"
        >
          {/* Surfaced TMDB-key CTA — every recommendation source on this
              screen depends on the user's own TMDB key, so an empty key
              would otherwise leave every row showing the generic
              "TMDB is unavailable" hint with nothing actionable. */}
          {!hasTmdbKey && (
            <div className="mx-4 rounded-xl border border-accent-primary/40 bg-accent-primary/10 px-4 py-3 text-sm text-text-primary">
              <p className="font-semibold">
                Add your TMDB API key to see recommendations
              </p>
              <p className="mt-1 text-xs text-text-secondary">
                Recommendations, trending titles, and genre rows all need a
                TMDB key. Open the Settings sheet from the library header to
                paste yours in &mdash; it&apos;s free and takes a minute.
              </p>
            </div>
          )}
          {/* Top for You — Movies */}
          <HomeRow
            title="Top for You — Movies"
            subtitle={
              recs.basedOn.movies.length > 0
                ? `From ${recs.basedOn.movies.length} movies you rated 6+`
                : 'Rate a few movies to unlock personalized picks'
            }
            count={recs.movies.length}
            loading={recs.loading}
            emptyHint="Rate a few movies to unlock personalized picks."
          >
            {recommendedMovieItems.map((it) => (
              <HomeTmdbPosterCard
                key={`tfy-m-${it.tmdbId}`}
                item={it}
                userRating={userMovieRatings.get(it.tmdbId) ?? null}
                onClick={() => onOpenDetail(it.tmdbId, 'movie')}
              />
            ))}
            {canLoadMoreMovies && (
              <LoadMoreCard
                onClick={() => setMovieVisible((v) => v + PAGE_SIZE)}
                hint={`+${Math.min(PAGE_SIZE, recs.movies.length - movieVisible)} more`}
              />
            )}
          </HomeRow>

          {/* Top for You — Series */}
          <HomeRow
            title="Top for You — Series"
            subtitle={
              recs.basedOn.series.length > 0
                ? `From ${recs.basedOn.series.length} series you rated 6+`
                : 'Rate a few series to unlock personalized picks'
            }
            count={recs.series.length}
            loading={recs.loading}
            emptyHint="Rate a few series to unlock personalized picks."
          >
            {recommendedSeriesItems.map((it) => (
              <HomeTmdbPosterCard
                key={`tfy-s-${it.tmdbId}`}
                item={it}
                userRating={userSeriesRatings.get(it.tmdbId) ?? null}
                onClick={() => onOpenDetail(it.tmdbId, 'tv')}
              />
            ))}
            {canLoadMoreSeries && (
              <LoadMoreCard
                onClick={() => setSeriesVisible((v) => v + PAGE_SIZE)}
                hint={`+${Math.min(PAGE_SIZE, recs.series.length - seriesVisible)} more`}
              />
            )}
          </HomeRow>

          {/* Browse by Genre */}
          <GenreRotatingRow
            selectedGenreId={selectedGenreId}
            onGenreChange={setSelectedGenreId}
            onOpenDetail={onOpenDetail}
            userMovieRatings={userMovieRatings}
            libraryMovieIds={libraryMovieIds}
          />

          {/* Popular (mixed movies + series) */}
          <HomeRow
            title="Popular"
            subtitle="This week's most-watched on TMDB — movies and series"
            count={trending.items.length}
            loading={trending.loading}
            emptyHint="TMDB is unavailable right now."
          >
            {trendingItems.map((it) => {
              const userRating =
                it.mediaType === 'movie'
                  ? userMovieRatings.get(it.tmdbId) ?? null
                  : userSeriesRatings.get(it.tmdbId) ?? null
              return (
                <HomeTmdbPosterCard
                  key={`pop-${it.mediaType}-${it.tmdbId}`}
                  item={it}
                  userRating={userRating}
                  onClick={() => onOpenDetail(it.tmdbId, it.mediaType)}
                />
              )
            })}
            {canLoadMoreTrending && (
              <LoadMoreCard
                onClick={() => setTrendingVisible((v) => v + PAGE_SIZE)}
                hint={`+${Math.min(PAGE_SIZE, trending.items.length - trendingVisible)} more`}
              />
            )}
          </HomeRow>
        </div>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────
// Oversized search bar — the primary home CTA.
// ────────────────────────────────────────────────────────────────────

function HomeSearchBar({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-4 rounded-2xl border border-border-primary bg-bg-secondary/70 px-6 py-5 text-left text-text-tertiary shadow-cinematic backdrop-blur transition-all hover:border-accent-primary/60 hover:bg-bg-tertiary hover:text-text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/60 sm:py-6"
      aria-label="Search movies and series"
      style={{ fontSize: 'clamp(0.9rem, 1.4vw, 1.125rem)' }}
    >
      <SearchIconLarge />
      <span className="flex-1 truncate font-medium">
        Search movies and series…
      </span>
      <kbd className="hidden rounded border border-border-primary bg-bg-primary/60 px-2 py-1 text-[11px] font-semibold text-text-tertiary sm:inline">
        Ctrl K
      </kbd>
    </button>
  )
}

function SearchIconLarge() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="shrink-0"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}

function GridIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  )
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`transition-transform ${open ? 'rotate-180' : ''}`}
      aria-hidden
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  )
}

const DOWNLOAD_BANNER_DISMISSED_KEY = 'govu.pwa.downloadBannerDismissed.v1'

function DownloadBanner() {
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false
    try {
      return window.localStorage.getItem(DOWNLOAD_BANNER_DISMISSED_KEY) === '1'
    } catch {
      return false
    }
  })

  if (dismissed) return null

  const handleDismiss = () => {
    setDismissed(true)
    try {
      window.localStorage.setItem(DOWNLOAD_BANNER_DISMISSED_KEY, '1')
    } catch {
      /* localStorage disabled — at least hide for this session */
    }
  }

  return (
    <div className="relative overflow-hidden rounded-xl border border-accent-primary/40 bg-gradient-to-r from-accent-primary/15 via-accent-primary/10 to-transparent px-5 py-4 shadow-sm">
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-primary/20 text-accent-primary">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary">
            Get the full Govu experience on desktop
          </p>
          <p className="mt-0.5 text-xs text-text-secondary">
            AI search, subtitles, local library scanning, and offline playback — all in the desktop installer.
          </p>
        </div>
        <Link
          to="/download"
          className="shrink-0 rounded-md bg-accent-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-accent-primary/90"
        >
          Download
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded-md p-1.5 text-text-tertiary transition-colors hover:bg-bg-secondary hover:text-text-primary"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  )
}
